/**
 * Normalize Chart of Account form/API body for IO.REST.MSG.ChartOfAccountRequest (%JSONImport).
 * Checkbox booleans and empty strings on %Integer fields cause IRIS ERROR #9406.
 * Mirrors property types in MBengine_ENS IO.REST.MSG.ChartOfAccountRequest.cls — 15/09/2026 PK.
 */

const COA_INTEGER_FIELDS: readonly string[] = [
  'id',
  'coaGid',
  'coaRno',
  'chartOfAccountType',
  'natureOfAccount',
  'chartOfAccountClassification',
  'ledgerPrintMode',
  'useGid',
  'useRno',
];

const COA_INTEGER_CHECKBOX_FIELDS: readonly string[] = [
  'nominalChartAccount',
  'vatApplicable',
];

const COA_STRING_FIELDS: readonly string[] = [
  'nominalAccountNo',
  'description',
  'absoluteTotalValue',
  'selectedForSample',
];

const COA_OMIT_FIELDS: readonly string[] = ['recordDate', 'recordTime'];

function isEmptyValue(value: unknown): boolean {
  return value === '' || value === null || value === undefined;
}

function coerceInteger(value: unknown, fromCheckbox = false): number | undefined {
  if (isEmptyValue(value)) {
    return fromCheckbox ? 0 : undefined;
  }
  if (typeof value === 'boolean') {
    return fromCheckbox ? (value ? 1 : 0) : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return fromCheckbox ? 0 : undefined;
    }
    const n = Number(trimmed);
    return Number.isNaN(n) ? undefined : Math.trunc(n);
  }
  return undefined;
}

/**
 * Build a ChartOfAccountRequest-shaped JSON body safe for %JSONImport (create/update).
 */
export function normalizeChartOfAccountForApi(
  body: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const checkboxSet = new Set(COA_INTEGER_CHECKBOX_FIELDS);

  for (const key of COA_STRING_FIELDS) {
    if (!(key in body)) {
      continue;
    }
    const v = body[key];
    if (isEmptyValue(v)) {
      continue;
    }
    out[key] = String(v);
  }

  for (const key of COA_INTEGER_FIELDS) {
    if (!(key in body)) {
      continue;
    }
    const n = coerceInteger(body[key], checkboxSet.has(key));
    if (n !== undefined) {
      out[key] = n;
    }
  }

  for (const key of COA_INTEGER_CHECKBOX_FIELDS) {
    if (!(key in body)) {
      continue;
    }
    const n = coerceInteger(body[key], true);
    if (n !== undefined) {
      out[key] = n;
    }
  }

  for (const key of COA_OMIT_FIELDS) {
    delete out[key];
  }

  return out;
}
